/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: RoleProfilesColumns.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Custom column renderers for Role Profiles DataGrid.
────────────────────────────────────────────────────────────────*/

import { useMemo } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { RoleProfile } from '../types';
import { Shield } from 'lucide-react';

export function useRoleProfilesColumnRenderers() {
  const { t, i18n } = useI18n();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPermissionLevelColor = (level: string) => {
    switch (level) {
      case 'FULL': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20';
      case 'READ': return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20';
      case 'NONE': return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20';
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20';
    }
  };

  const getColumnRenderer = useMemo(() => {
    return (key: string) => {
      switch (key) {
        case 'name':
          return (value: unknown, item: unknown) => {
            const profile = item as RoleProfile;
            return (
              <div className="flex items-center gap-2">
                <div className="font-medium text-foreground">{value as string}</div>
                {profile.is_system && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                    title={t('profiles.systemProfile')}
                  >
                    <Shield className="h-2.5 w-2.5" />
                    {t('profiles.primaryProfileShort')}
                  </span>
                )}
              </div>
            );
          };
        case 'description':
          return (value: unknown) => (
            <div className="text-muted-foreground">{value as string || '-'}</div>
          );
        case 'usersCount':
          return (value: unknown) => (
            <div className="text-foreground">{value as number}</div>
          );
        case 'created_at':
          return (value: unknown) => (
            <div className="text-foreground">{formatDate(value as string)}</div>
          );
        case 'updated_at':
          return (value: unknown) => (
            <div className="text-foreground">{formatDate(value as string)}</div>
          );
        case 'permissions':
          return (value: unknown, item: unknown) => {
            const profile = item as RoleProfile;
            const activePermissions = profile.permissions.filter((p) => p.level !== 'NONE');
            return (
              <div className="flex flex-wrap gap-1">
                {activePermissions.slice(0, 3).map((permission, index) => {
                  const translatedName = permission.moduleSlug
                    ? t(`menu.sections.${permission.moduleSlug}`, permission.moduleName)
                    : permission.moduleName || permission.moduleSlug || 'Unknown';

                  return (
                    <span
                      key={index}
                      className={`px-2 py-1 text-xs rounded-full ${getPermissionLevelColor(permission.level)}`}
                      aria-label={`${translatedName}: ${permission.level}`}
                    >
                      {translatedName.length > 15 ? translatedName.substring(0, 15) + '...' : translatedName}
                    </span>
                  );
                })}
                {activePermissions.length > 3 && (
                  <span className="px-2 py-1 text-xs text-muted-foreground">
                    +{activePermissions.length - 3} {t('profiles.morePermissions')}
                  </span>
                )}
                {activePermissions.length === 0 && (
                  <span className="text-xs text-muted-foreground">{t('profiles.noActivePermissions')}</span>
                )}
              </div>
            );
          };
        default:
          return undefined;
      }
    };
  }, [t, i18n.language]);

  return { getColumnRenderer };
}
